import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { calculateTotalBcrs, calculateTotalBcrsDeposit } from "../../common/helpers/bcrsHelper";
import { displayCentsWithDollarSign } from "../../common/helpers/stringUtils";
import { useItemSubtotal } from "../../common/hooks/useItemSubtotal";
import CheckoutButton from "../../components/CheckoutButton/CheckoutButton";
import Condition from "../../components/Conditional/Condition";
import QuantitySelector from "../../components/elements/Selection/quantity/QuantitySelector";
import {
	useCartActions,
	useCartContext,
} from "../../contexts/CartServiceContext/CartServiceContext";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { PATH } from "../../router/path";
import { addDatadogAction, buildLocationContext } from "../../services/datadog";
import "./CartPage.scss";

const CartPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { state: cart } = useCartContext((state) => state.cart);
	const { REMOVE_CART, UPDATE_CART_ITEM } = useCartActions();
	const { state: bcrsDepositRate } = useConfigContext((state) => state.config.bcrs_deposit_rate);
	const { state: location } = useConfigContext((state) => state.location);
	const { state: stall } = useConfigContext((state) => state.config.stall);

	const subtotal = useItemSubtotal(cart);

	const cartItemCount = useMemo(
		() => cart.reduce((total, item) => total + item.quantity, 0),
		[cart]
	);

	useEffect(() => {
		addDatadogAction("cart_page_mount", {
			...buildLocationContext(location, stall),
			cartItemCount,
			cartTotal: subtotal,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleGoBack = () => {
		addDatadogAction("cart_back_to_menu", {
			...buildLocationContext(location, stall),
			cartItemCount,
		});
		navigate(PATH.PRODUCT_LIST);
	};

	const handleRemoveItem = (index: number) => {
		const removed = cart[index];

		addDatadogAction("cart_remove_item", {
			...buildLocationContext(location, stall),
			itemIndex: index,
			sku: removed ? String(removed.brand_menu_item_id) : undefined,
			productName: removed?.title,
		});
		REMOVE_CART(index);
	};

	const handleQuantityChange = (index: number) => (quantity: number) => {
		const previous = cart[index];

		if (quantity === 0) {
			addDatadogAction("cart_remove_item", {
				...buildLocationContext(location, stall),
				itemIndex: index,
				sku: previous ? String(previous.brand_menu_item_id) : undefined,
				productName: previous?.title,
			});
			REMOVE_CART(index);
		} else {
			addDatadogAction("cart_quantity_change", {
				...buildLocationContext(location, stall),
				itemIndex: index,
				sku: previous ? String(previous.brand_menu_item_id) : undefined,
				previousQty: previous?.quantity,
				newQty: quantity,
			});
			const updatedItem = { ...cart[index], quantity };

			UPDATE_CART_ITEM({ index, item: updatedItem });
		}
	};

	// Calculate BCRS deposit amount
	const bcrsDepositAmount = useMemo(() => {
		return calculateTotalBcrsDeposit(cart, bcrsDepositRate);
	}, [cart, bcrsDepositRate]);

	const totalBcrs = useMemo(() => {
		return calculateTotalBcrs(cart);
	}, [cart]);

	return (
		<div className="CartPage">
			{/* Top Header */}
			<div className="CartHeader">
				<h1 className="CartTitle">{t("Cart.title")}</h1>
			</div>

			{/* Cart Content */}
			<div className="CartContent">
				{cart.length === 0 ? (
					<div className="EmptyCart">
						<p>{t("Cart.empty")}</p>
					</div>
				) : (
					<div className="CartItems">
						{cart.map((item, index) => (
							<div key={`${item.brand_menu_item_id}-${index}`} className="CartItem">
								<div className="CartItemContent">
									<button className="RemoveButton" onClick={() => handleRemoveItem(index)}>
										×
									</button>

									<div className="ItemImage">
										<img
											src={item.product_image || "/placeholder.png"}
											alt={item.title}
											draggable={false}
										/>
									</div>

									<div className="ItemDetails">
										<h3 className="ItemName">{item.title}</h3>

										<div className="ItemControls">
											<QuantitySelector
												max={99}
												onChange={handleQuantityChange(index)}
												initValue={item.quantity}
												testid={`cart-item-${item.brand_menu_item_id}`}
												inventoryCount={item.inventory_count}
											/>
										</div>
									</div>

									<div className="ItemPrice">
										${((item.price * item.quantity) / 100).toFixed(2)}
									</div>
								</div>

								<Condition check={calculateTotalBcrsDeposit([item], bcrsDepositRate) > 0}>
									<div className="CartAdditionInfo">
										<div className="AdditionInfoItem">
											<span className="AdditionLabel">
												BCRS Deposit*
												{(item.bcrs > 1 || item.quantity > 1) && ` x ${item.quantity * item.bcrs}`}
											</span>
											<span className="AdditionValue">
												{displayCentsWithDollarSign(
													calculateTotalBcrsDeposit([item], bcrsDepositRate)
												)}
											</span>
										</div>
									</div>
								</Condition>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Bottom Section */}
			<div className="CartBottom">
				<div className="TotalSection">
					<Condition check={totalBcrs > 0}>
						<div className="BcrsLine">
							<div className="BcrsItemHeader">
								<span className="BcrsItemName">BCRS Deposit*</span>
								<span className="BcrsPrice">{displayCentsWithDollarSign(bcrsDepositAmount)}</span>
							</div>
							<div className="BcrsDisclaimer">{t("cartPage.bcrs.disclaimer")}</div>
						</div>
					</Condition>
					<div className="TotalLine">
						<span>{t("paymentPage.subtotal")}</span>
						<span>${(subtotal / 100).toFixed(2)}</span>
					</div>
					<div className="SubtotalNote">{t("cartPage.subtotalNote")}</div>
				</div>

				<div className="ActionButtons">
					<button className="BackToMenuButton" onClick={handleGoBack}>
						<span>
							{"<"} {t("Cart.backToMenu")}
						</span>
					</button>
					<Condition check={cart.length > 0}>
						<div
							className="CheckoutButtonWrap"
							onClickCapture={() => {
								addDatadogAction("cart_checkout_initiated", {
									...buildLocationContext(location, stall),
									cartItemCount,
									cartTotal: subtotal,
								});
							}}
						>
							<CheckoutButton className="ContinuePaymentButton" />
						</div>
					</Condition>
				</div>
			</div>
		</div>
	);
};

export default CartPage;
